import { STUNTS } from '../config/tuning';

export type StuntEvent =
  | { type: 'flip'; direction: 'back' | 'front'; coins: number; fuel: number }
  | { type: 'airtime'; seconds: number; coins: number };

const TWO_PI = 6.283185307179586;

/**
 * Detects flips and airtime from wheel contact and chassis angular velocity.
 * Rotation is integrated from angular velocity (arithmetic only) rather than read with atan2,
 * so flip counts are deterministic across platforms.
 */
export class StuntTracker {
  private airborneSeconds = 0;
  private rotation = 0;
  private flipsThisJump = 0;

  /** Returns the stunt events completed this tick. */
  update(anyWheelGrounded: boolean, angularVelocityZ: number, dt: number): StuntEvent[] {
    const events: StuntEvent[] = [];
    if (!anyWheelGrounded) {
      this.airborneSeconds += dt;
      this.rotation += angularVelocityZ * dt;
      // A completed full rotation pays out immediately so the player sees it mid-air.
      while (Math.abs(this.rotation) >= TWO_PI * (this.flipsThisJump + 1)) {
        this.flipsThisJump += 1;
        events.push({
          type: 'flip',
          direction: this.rotation > 0 ? 'back' : 'front',
          coins: STUNTS.flipCoins,
          fuel: STUNTS.flipFuel,
        });
      }
      return events;
    }

    if (this.airborneSeconds >= STUNTS.airtimeThreshold) {
      const extraHalves = Math.floor((this.airborneSeconds - STUNTS.airtimeThreshold) / 0.5) + 1;
      events.push({
        type: 'airtime',
        seconds: this.airborneSeconds,
        coins: extraHalves * STUNTS.airtimeCoinsPerHalfSecond,
      });
    }
    this.reset();
    return events;
  }

  get airborne(): boolean {
    return this.airborneSeconds >= STUNTS.airborneGraceSeconds;
  }

  get airSeconds(): number {
    return this.airborneSeconds;
  }

  reset(): void {
    this.airborneSeconds = 0;
    this.rotation = 0;
    this.flipsThisJump = 0;
  }
}
